import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterSidebar = ({ filters, onFiltersChange, onClearAll, isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    material: true,
    capacity: true,
    color: true,
    price: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleFilterChange = (category, value, checked) => {
    const currentFilters = filters?.[category] || [];
    const newFilters = checked 
      ? [...currentFilters, value]
      : currentFilters?.filter(item => item !== value);
    
    onFiltersChange({
      ...filters,
      [category]: newFilters
    });
  };

  const handlePriceChange = (priceRange) => {
    onFiltersChange({
      ...filters,
      priceRange
    });
  };

  const filterSections = [
    {
      id: 'category',
      title: 'Product Category',
      icon: 'Package',
      options: [
        { value: 'steel-heritage', label: 'Steel Heritage' },
        { value: 'smart-innovation', label: 'Smart Innovation' },
        { value: 'insulated-performance', label: 'Insulated Performance' },
        { value: 'limited-editions', label: 'Limited Editions' },
        { value: 'future-releases', label: 'Future Releases' }
      ]
    },
    {
      id: 'material',
      title: 'Material Type',
      icon: 'Layers',
      options: [
        { value: 'stainless-steel', label: 'Stainless Steel' },
        { value: 'borosilicate-glass', label: 'Borosilicate Glass' },
        { value: 'titanium', label: 'Titanium' },
        { value: 'copper-lined', label: 'Copper Lined' },
        { value: 'bamboo-fiber', label: 'Bamboo Fiber' }
      ]
    },
    {
      id: 'capacity',
      title: 'Capacity Range',
      icon: 'Droplets',
      options: [
        { value: '350ml', label: '350ml - Compact' },
        { value: '500ml', label: '500ml - Standard' },
        { value: '750ml', label: '750ml - Large' },
        { value: '1000ml', label: '1L - Extra Large' },
        { value: '1500ml', label: '1.5L - Family Size' }
      ]
    },
    {
      id: 'color',
      title: 'Color Options',
      icon: 'Palette',
      options: [
        { value: 'mava-orange', label: 'Mava Orange' },
        { value: 'ocean-blue', label: 'Ocean Blue' },
        { value: 'forest-green', label: 'Forest Green' },
        { value: 'midnight-black', label: 'Midnight Black' },
        { value: 'pearl-white', label: 'Pearl White' },
        { value: 'sunset-gradient', label: 'Sunset Gradient' }
      ]
    }
  ];

  const priceRanges = [
    { value: '0-1000', label: '₹0 - ₹1,000' },
    { value: '1000-2500', label: '₹1,000 - ₹2,500' },
    { value: '2500-5000', label: '₹2,500 - ₹5,000' },
    { value: '5000-10000', label: '₹5,000 - ₹10,000' },
    { value: '10000+', label: '₹10,000+' }
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <div className={`
        fixed lg:sticky top-0 left-0 h-screen lg:h-auto w-80 lg:w-full
        bg-card border-r lg:border-r-0 lg:border border-border
        transform transition-transform duration-300 ease-in-out z-50 lg:z-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        overflow-y-auto lg:overflow-visible
      `}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Filter" size={18} className="text-primary" />
              </div>
              <h3 className="text-lg font-poppins font-semibold text-text-primary">Filters</h3>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="text-text-secondary hover:text-primary"
              >
                Clear All
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="lg:hidden"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Filter Sections */}
          <div className="space-y-6">
            {filterSections?.map((section) => (
              <div key={section?.id} className="border-b border-border pb-6 last:border-b-0">
                <button
                  onClick={() => toggleSection(section?.id)}
                  className="flex items-center justify-between w-full mb-4 group"
                >
                  <div className="flex items-center space-x-3">
                    <Icon 
                      name={section?.icon} 
                      size={16} 
                      className="text-primary group-hover:text-accent brand-transition" 
                    />
                    <span className="font-inter font-medium text-text-primary">
                      {section?.title}
                    </span>
                  </div>
                  <Icon 
                    name={expandedSections?.[section?.id] ? "ChevronUp" : "ChevronDown"} 
                    size={16} 
                    className="text-text-secondary group-hover:text-primary brand-transition" 
                  />
                </button>

                {expandedSections?.[section?.id] && (
                  <div className="space-y-3 pl-7">
                    {section?.options?.map((option) => (
                      <Checkbox
                        key={option?.value}
                        label={option?.label}
                        checked={(filters?.[section?.id] || [])?.includes(option?.value)}
                        onChange={(e) => handleFilterChange(section?.id, option?.value, e?.target?.checked)}
                        className="text-sm"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Price Range Section */}
            <div className="border-b border-border pb-6 last:border-b-0">
              <button
                onClick={() => toggleSection('price')}
                className="flex items-center justify-between w-full mb-4 group"
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name="IndianRupee" 
                    size={16} 
                    className="text-primary group-hover:text-accent brand-transition" 
                  />
                  <span className="font-inter font-medium text-text-primary">
                    Price Range
                  </span>
                </div>
                <Icon 
                  name={expandedSections?.price ? "ChevronUp" : "ChevronDown"} 
                  size={16} 
                  className="text-text-secondary group-hover:text-primary brand-transition" 
                />
              </button>

              {expandedSections?.price && (
                <div className="space-y-3 pl-7">
                  {priceRanges?.map((range) => (
                    <Checkbox
                      key={range?.value}
                      label={range?.label}
                      checked={filters?.priceRange === range?.value}
                      onChange={(e) => handlePriceChange(e?.target?.checked ? range?.value : null)}
                      className="text-sm"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Apply Filters Button (Mobile) */}
          <div className="mt-8 lg:hidden">
            <Button
              variant="default"
              fullWidth
              onClick={onClose}
              className="bg-primary hover:bg-primary/90"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;